import { useState } from 'react'
import { Plus as PlusIcon } from 'react-feather'
import {
  Box,
  Button,
  Tab,
  TablePagination,
  Tabs,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import { useTheme } from '@material-ui/styles'
import { useHistory } from 'react-router-dom'

import {
  Card,
  ConfirmationDialog,
  Container,
  ContentHeader,
  FilterButton,
  LoadingFeedback,
  Page,
  Permitted,
} from 'components'
import { RegulationFilter, RegulationsTable } from './components'

import helpers from 'helpers'

import { useFetch, useFilter, usePagination, useSnackbar } from 'hooks'

import { routes } from 'Routes'
import * as service from 'service'
import constants from 'constants/index'
import { Alert } from '@material-ui/lab'

const RegulationsMain = () => {
  const [saving, setSaving] = useState(false)
  const [openChangeStatusDialog, setOpenChangeStatusDialog] = useState(false)
  const [selectedRegulation, setSelectedRegulation] = useState(null)

  const filter = useFilter()
  const snackbar = useSnackbar()

  const { STATUSES, TABS } = constants.regulations
  const { REGULATIONS } = constants.permissions

  const { status, ...filters } = filter.filters

  const tab = status || 'all'

  const theme = useTheme()
  const history = useHistory()
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), {
    defaultMatches: true,
  })

  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    usePagination(10)

  const { response, isLoading, refresh } = useFetch(
    service.backoffice.regulations.get,
    { ...filter.filters, perPage, page },
    [filter.filters, page, perPage],
  )

  const regulations = response?.data?.regulations || []
  const totalCount = response?.data?.meta?.totalCount || 0

  const handleChangeTab = (_, value) => {
    filter.setFilters(value === 'all' ? filters : { ...filters, status: value })
  }

  const handleRedirection = () => history.push(routes.regulations.new)

  const handleOpenChangeStatusDialog = (regulationId) => {
    const regulation = regulations.find(
      (regulation) => regulation?.id === regulationId,
    )

    setSelectedRegulation(regulation)
    setOpenChangeStatusDialog(true)
  }

  const handleChangeStatus = async () => {
    try {
      setSaving(true)

      const isInactive = selectedRegulation?.status === STATUSES.INACTIVE

      await service.backoffice.regulations.update({
        regulationId: selectedRegulation?.id,
        status: isInactive ? STATUSES.DRAFT : STATUSES.INACTIVE,
      })

      snackbar.open({
        message: 'Status do regulamento atualizado com sucesso!',
        variant: 'success',
      })

      refresh()
      setOpenChangeStatusDialog(false)
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Falha ao atualizar status do regulamento!',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Page title="Listagem de Regulamentos">
      <LoadingFeedback open={isLoading || saving} />
      <Container
        maxWidth={false}
        spacingXl={30}
        spacingLg={25}
        spacingMd={10}
        spacingSm={10}
      >
        <ContentHeader
          title="Listagem"
          menu="Documentos"
          subtitle="Listagem de Regulamentos"
        >
          <Box ml={1}>
            <FilterButton setDrawerOpen={filter.setDrawerOpen} />
          </Box>
        </ContentHeader>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          indicatorColor="primary"
          textColor="primary"
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.value}
              label={tab.label}
              value={tab.value}
              onClick={(event) => handleChangeTab(event, tab.value)}
            />
          ))}
        </Tabs>
        <Card
          title="Regulamentos"
          fromTable
          withPagination
          actionButton={
            <Permitted tag={REGULATIONS.MANAGE}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PlusIcon />}
                onClick={handleRedirection}
              >
                Criar
              </Button>
            </Permitted>
          }
        >
          <RegulationsTable
            regulations={regulations}
            onChangeStatus={handleOpenChangeStatusDialog}
          />
          <Box px={2} display="flex" justifyContent="flex-end">
            <TablePagination
              component="div"
              count={totalCount}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              page={page - 1}
              rowsPerPage={perPage}
              rowsPerPageOptions={[5, 10, 25, 100]}
              labelRowsPerPage={isDesktop ? 'Por página' : ''}
              nextIconButtonProps={{ size: 'small' }}
              backIconButtonProps={{ size: 'small' }}
            />
          </Box>
        </Card>
      </Container>
      <RegulationFilter key={JSON.stringify(filter.filters)} filter={filter} />
      <ConfirmationDialog
        open={openChangeStatusDialog}
        setOpen={setOpenChangeStatusDialog}
        message={
          selectedRegulation?.status === STATUSES.INACTIVE
            ? 'Deseja realmente ativar este regulamento?'
            : 'Deseja realmente inativar este regulamento?'
        }
        cancelButtonText="cancelar"
        buttonText={
          selectedRegulation?.status === STATUSES.INACTIVE
            ? 'Ativar'
            : 'Inativar'
        }
        actionAcceptButton={handleChangeStatus}
      />
    </Page>
  )
}

export default RegulationsMain
